import type { TransportOperationName } from "@/types_api/backend/transport/operations"

import { transportOperations } from "./transportOperationRuntime"
import type {
  TransportHookRequest,
  TransportInfiniteOperationQueryKey,
  TransportInfiniteQueryKey,
  TransportOperationQueryKey,
  TransportPaginatedOperationQueryKey,
  TransportPaginatedQueryKey,
  TransportQueryKey,
} from "./transportQueryTypes"

const TRANSPORT_QUERY_KEY_ROOT = "transport"
const TRANSPORT_PAGINATED_QUERY_KEY_ROOT = "transport-paginated"
const TRANSPORT_INFINITE_QUERY_KEY_ROOT = "transport-infinite"

export function getTransportQueryKey<Name extends TransportOperationName>(
  operation: Name,
  request?: TransportHookRequest<Name>,
  organizationSlug: string | null = null,
  workspaceId: string | null = null,
): TransportQueryKey<Name> {
  return [
    ...getTransportOperationQueryKey(operation, organizationSlug, workspaceId),
    request ?? null,
  ]
}

export function getTransportPaginatedQueryKey<
  Name extends TransportOperationName,
>(
  operation: Name,
  request?: TransportHookRequest<Name>,
  organizationSlug: string | null = null,
  workspaceId: string | null = null,
  queryKeyScope?: readonly unknown[],
): TransportPaginatedQueryKey<Name> {
  const requestKey = [
    ...getTransportPaginatedOperationQueryKey(
      operation,
      organizationSlug,
      workspaceId,
    ),
    request ?? null,
  ] as const

  return queryKeyScope === undefined
    ? requestKey
    : [...requestKey, queryKeyScope]
}

export function getTransportInfiniteQueryKey<
  Name extends TransportOperationName,
>(
  operation: Name,
  request?: TransportHookRequest<Name>,
  organizationSlug: string | null = null,
  workspaceId: string | null = null,
): TransportInfiniteQueryKey<Name> {
  return [
    ...getTransportInfiniteOperationQueryKey(
      operation,
      organizationSlug,
      workspaceId,
    ),
    request ?? null,
  ]
}

export function getTransportOperationQueryKey<
  Name extends TransportOperationName,
>(
  operation: Name,
  organizationSlug: string | null = null,
  workspaceId: string | null = null,
): TransportOperationQueryKey<Name> {
  return [TRANSPORT_QUERY_KEY_ROOT, operation, organizationSlug, workspaceId]
}

export function getTransportPaginatedOperationQueryKey<
  Name extends TransportOperationName,
>(
  operation: Name,
  organizationSlug: string | null = null,
  workspaceId: string | null = null,
): TransportPaginatedOperationQueryKey<Name> {
  return [
    TRANSPORT_PAGINATED_QUERY_KEY_ROOT,
    operation,
    organizationSlug,
    workspaceId,
  ]
}

export function getTransportInfiniteOperationQueryKey<
  Name extends TransportOperationName,
>(
  operation: Name,
  organizationSlug: string | null = null,
  workspaceId: string | null = null,
): TransportInfiniteOperationQueryKey<Name> {
  return [
    TRANSPORT_INFINITE_QUERY_KEY_ROOT,
    operation,
    organizationSlug,
    workspaceId,
  ]
}

export function getTransportQueryWorkspaceId<
  Name extends TransportOperationName,
>(operation: Name, workspaceId: string | null): string | null {
  const operationDefinition = transportOperations[operation]

  if (
    !operationDefinition.organizationRequired ||
    operationDefinition.resource === "workspaces"
  ) {
    return null
  }

  return workspaceId
}

export function getTransportQueryOrganizationSlug<
  Name extends TransportOperationName,
>(operation: Name, organizationSlug: string | null): string | null {
  const operationDefinition = transportOperations[operation]

  if (
    operationDefinition.auth !== "session" &&
    !operationDefinition.organizationRequired
  ) {
    return null
  }

  return organizationSlug
}
